import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { updateActivityTrackerArrayforClob } from "utils/ClobDataUserActivityTracker";

import {
  POLICY_STATUS_DELETED,
  POLICY_STATUS_EDIT,
  POLICY_STATUS_NEW,
  POLICY_STATUS_VARIANCE,
  POLICY_STATUS_NEW_OR_VARIANCE,
  tabOrder,
  DUHS_RFG,
  ALL,
  FAILED_VALIDATION,
  PXDX,
  HISTORY_TRANSLATION,
  DEPENDENCY,
  INCOMPATIBILITY,
  DEMOGRAPHICS,
} from "./Constants/PolicyTranslationConstants";
import {
  filterRules,
  getAvailableProcedureCodes,
  getCoverageCriteriaList,
  getLobList,
  getTabLabels,
} from "./Utils/TranslationUtils";
import {
  initializeWorkspaceController,
  updatePolicyData,
} from "../../utils/WorkspaceUtils";
import TopPanelComponent from "./TopPanelComponent";
import EndDateDialog from "./Dialogs/EndDateDialog";
import { TranslationFilter } from "./Filters/FiltersComponent";
import PolicyTabsComponent from "./PolicyTabs/PolicyTabsComponent";
import emptyRowTemplate from "./Constants/EmptyRowTemplate.json";
import { useWorkspace } from "context/WorkspaceContext";
import { Box, selectClasses } from "@mui/material";
import useGridResizeHook, {
  calculateGridHeight,
} from "hooks/useGridResizeHook";
import {
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import api from "../../api";
import { setDialog } from "slices/appSlice";
import { formErrorStrfromObj, sanitizeCC, compareCCValues } from "utils/shared";
import {
  duplicateLinkedRules,
  generateHistoryRules,
  updateLinkedRulesAfterEdit,
} from "./Utils/LinkedRulesUtils";
import {
  validatedDemographics,
  validatedDependency,
  validatedHistoryTranslation,
  validatedIncompatibility,
  validatePXDX,
} from "./Validation/ValidationHelper";

import { NEW_COMMENT, WORKFLOW_TRANSLATION } from "constants/Constants";

const PolicyTranslation = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const topPanelRef = useRef(null);
  const [gridHeight, setGridHeight] = useState("auto");
  const policyState = location?.state?.policyState;

  const { policyNumber } = location.state;
  const { policyTitle } = location.state;

  const { workspaceController, setWorkspaceController } = useWorkspace();

  const editable = policyState.editMode || false;
  const [availableProcedureCodes, setAvailableProcedureCodes] = useState([]);
  const [selectedCC, setSelectedCC] = useState(ALL);
  const [selectedvalidation, setselectedvalidation] = useState(ALL);

  const [selectedLob, setSelectedLob] = useState(ALL);
  const [selectedVariance, setSelectedVariance] = useState(ALL);
  const [selectedUseCase, setSelectedUseCase] = useState(ALL);
  const [newRowStructure] = useState(emptyRowTemplate);
  const [coverageCriteriaList, setCoverageCriteriaList] = useState([]);
  const [lobList, setLobList] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [dynamicTabs, setDynamicTabs] = useState(getTabLabels());
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [startedFromScratch, setStartedFromScratch] = useState(false);
  const [policyData, setPolicyData] = useState({
    policyTranslator: "",
    translationReviewer: "",
    translationStatus: "",
    reviewType: "",
    overallAdoptionDecision: "",
    lobAdoptionDecisionList: [],
    policyChangeControlXrefDto: {
      policyChangeControlXrefKey: null,
      changeControlText: "",
    },
  });
  const [policyHistoryData, setPolicyHistoryData] = useState([]);
  const healthPlanKey = workspaceController?.workspace?.healthPlanKey;
  const policyKey = policyState?.policyKey;
  const [ctReviewData, setCTReviewData] = useState([]);

  const [submitForReview, setsubmitForReview] = useState(false);
  const [shouldSubmit, setshouldSubmit] = useState(false);
  const [reviewData, setReviewData] = useState({
    ctReviewKey: undefined,
    results: "",
    comments: "",
    reviewType: "",
  });
  const [activityUpdates, setActivityUpdates] = useState([]);

  const workspaceLoaded = workspaceController && workspaceController.workspace;
  const rulesList = workspaceLoaded
    ? workspaceController.getClobPolicyRulesList()
    : null;

  const policyRules = useMemo(() => {
    if (workspaceLoaded)
      return filterRules(
        rulesList,
        selectedCC,
        selectedVariance,
        selectedUseCase,
        selectedLob,
        policyState?.healthPlanName,
        setDynamicTabs
      );
    return null;
  }, [
    workspaceLoaded,
    rulesList,
    selectedCC,
    selectedVariance,
    selectedUseCase,
    selectedLob,
    policyState?.healthPlanName,
  ]);

  const resetFilter = (submitForReview) => {
    setSelectedLob(ALL);
    setSelectedVariance(ALL);
    setSelectedCC(ALL);
    setshouldSubmit(true);
    setsubmitForReview(submitForReview);
  };
  const filterResetted = useMemo(
    () =>
      selectedCC === ALL &&
      selectedLob === ALL &&
      selectedVariance === ALL &&
      selectedvalidation === ALL,
    [selectedCC, selectedLob, selectedVariance, selectedvalidation]
  );
  const [dxLimitationValues, setDxLimitationValues] = useState([]);
  const deletedRowIdRef = useRef(null);

  const dynamicMenuItems = dynamicTabs.filter((t) => !t.display);

  useEffect(() => {
    initializeWorkspaceController(workspaceController, setWorkspaceController);
  }, [workspaceController, setWorkspaceController]);

  useEffect(() => {
    if (policyRules != null && tabValue === -1) {
      let tableId = null;
      for (let table in policyRules) {
        if (policyRules[table].length > 0) {
          tableId = table;
          break;
        }
      }
      setTabValue(tabOrder.get(tableId));
    }
  }, [policyRules, tabValue]);

  const getDxLimitationValues = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.PolicyRules.getDxLimitationList();
      if (
        response.status === 200 &&
        response.data !== null &&
        response.data.length > 0
      ) {
        setDxLimitationValues(response.data);
      } else if (response.data == null || response.data.length === 0) {
        throw new Error("No Dx Limitation Values found.");
      }
    } catch (e) {
      dispatch(
        setDialog({
          title: "Error",
          message: formErrorStrfromObj(e),
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);
  useEffect(() => {
    if (workspaceLoaded) {
      setCoverageCriteriaList(
        getCoverageCriteriaList(workspaceController.getClobPolicyRulesList())
      );
      setLobList(getLobList(workspaceController.getClobPolicyRulesList()));
    }
  }, [dispatch, workspaceLoaded]);

  useEffect(() => {
    getDxLimitationValues();
  }, [getDxLimitationValues]);

  useEffect(() => {
    if (workspaceController != null && availableProcedureCodes.length === 0) {
      const codes = getAvailableProcedureCodes(
        workspaceController.getClobPolicyRulesList()
      );

      // Only set state if codes are actually non-empty and different
      if (codes.length > 0) {
        setAvailableProcedureCodes(codes);
      }
    }
  }, [workspaceController]);

  const addNewRows = async (checkedItems, ruleFlowGroup) => {
    const healthPlanDefaultId = policyState?.healthPlanDefaultId || "";
    const allNewRows = [];
    let activityUpdatesArray = activityUpdates || [];

    checkedItems.map((item) => {
      let newRow = JSON.parse(JSON.stringify(newRowStructure));
      newRow.RuleFacts.ProcedureCode = item.code;
      newRow.RuleFacts.ProcedureCodeDescription = item.codeDescription;
      newRow.RuleFacts.CoverageCriteriaList = item.coverageCriteriaList?.trim();
      newRow.RuleFacts.RuleEndDate = "";
      newRow.RuleFacts.AllowedTimeframe = "";
      newRow.RuleFacts.AgeRange = "";
      newRow.RuleFacts.DXLimitation = "";
      newRow.RuleFacts.DiagnosisListExpanded = item.DiagnosisListExpanded;
      newRow.RuleFacts.CodingClassification = item.CodingClassification;
      newRow.RuleFacts.DiagnosisListCondensed = item.DiagnosisListCondensed;
      newRow.PolicyHeader.PolicyNumber = item.PolicyNumber;
      newRow.PolicyHeader.PolicyDescription = policyTitle;
      newRow.PolicyHeader.PolicyVersion = policyState?.policyVersion;
      newRow.RuleHeader.ChangeControl = item.ChangeControl;
      if (healthPlanDefaultId === "AHCS") {
        newRow.RuleHeader.EditRule = POLICY_STATUS_NEW;
        newRow.RuleHeader.RuleVersion = "1.0";
      } else {
        newRow.RuleHeader.EditRule = POLICY_STATUS_VARIANCE;
        newRow.RuleHeader.RuleVersion = `1.0-${healthPlanDefaultId}-1.0`;
      }
      newRow.PolicyHeader.HealthPlanId = healthPlanDefaultId;
      newRow.RuleHeader.PolicyBusinessRuleName = "";
      if (ruleFlowGroup === DUHS_RFG) {
        //Setting default to 1
        newRow.RuleFacts.ApprovedUnitCount = "1";
        const generatedRules = generateHistoryRules(
          newRow,
          undefined,
          workspaceController
        );
        allNewRows.push(...generatedRules);
      } else {
        newRow.RuleHeader.RuleFlowGroup = ruleFlowGroup;
        allNewRows.push(newRow);
      }
    });

    try {
      const response =
        await api.policyData.getPolicyBusinesRuleNames(allNewRows);
      if (response.status === 200 && response.data !== null) {
        allNewRows.forEach((row, index) => {
          row.RuleHeader.PolicyBusinessRuleName = response.data[index].ruleName;

          //update the activity tracking array
          activityUpdatesArray = updateActivityTrackerArrayforClob(
            workspaceController?.workspace?.healthPlanKey,
            workspaceController?.workspace?.policyKey,
            "200",
            WORKFLOW_TRANSLATION,
            window.location.href,
            row.RuleHeader.RuleVersion,
            row.RuleHeader.ChangeControl,
            response.data[index].ruleName,
            row.RuleFacts.ProcedureCode,
            "add",
            "rule",
            activityUpdatesArray,
            row.RuleHeader.RuleFlowGroup
          );
        });
      } else {
        throw new Error("API failed to retrieve policy business rule names.");
      }
    } catch (e) {
      allNewRows.forEach((row) => {
        //update the activity tracking array
        activityUpdatesArray = updateActivityTrackerArrayforClob(
          workspaceController?.workspace?.healthPlanKey,
          workspaceController?.workspace?.policyKey,
          "200",
          WORKFLOW_TRANSLATION,
          window.location.href,
          row.RuleHeader.RuleVersion,
          row.RuleHeader.ChangeControl,
          "", //Rulename was not retreived from the API
          row.RuleFacts.ProcedureCode,
          "add",
          "Procedure Code",
          activityUpdatesArray,
          row.RuleHeader.RuleFlowGroup
        );
      });
      dispatch(
        setDialog({
          title: "Error",
          message: formErrorStrfromObj(e),
        })
      );
    }

    setActivityUpdates(activityUpdatesArray);

    setWorkspaceController((prevController) =>
      prevController.cloneController().addBusinessRule(allNewRows)
    );
  };

  const duplicateRow = (id) => {
    const index = workspaceController.clob.PolicyBusinessRulesList.findIndex(
      (p) => p.index === id
    );
    if (index === -1) return; // row not found, exit early

    const row = workspaceController.clob.PolicyBusinessRulesList[index];
    const flowGroup = row.RuleHeader?.RuleFlowGroup?.trim();
    const linkedHistoryKey = row.RuleHeader?.LinkedHistoryRuleKey;

    let newController = workspaceController.cloneController();
    if (flowGroup === DUHS_RFG && linkedHistoryKey) {
      newController = duplicateLinkedRules(index, workspaceController);
    } else {
      // Single row logic unchanged
      const duplicateIndex = index + 1;
      newController = newController
        .duplicateRule(index)
        .setEditRule(duplicateIndex, POLICY_STATUS_NEW_OR_VARIANCE)
        .incrementRuleVersion(duplicateIndex);
    }

    setWorkspaceController(newController);
  };

  /**
   * Adds the LobConfigKey to the existing row so that the end dates can be set.
   *
   * @param {Object} oldRow - The row object to update with the LobConfigKey.
   */
  const endExistingRule = (oldRow) => {
    const index = workspaceController.clob.PolicyBusinessRulesList.findIndex(
      (p) => p.index === oldRow.index
    );

    const endDate = oldRow.RuleFacts.RuleEndDate || "";

    const newController = workspaceController
      .cloneController()
      .setEditRule(index, POLICY_STATUS_EDIT)
      .setRule(index, oldRow);
    setWorkspaceController(newController);
  };

  const runValidationForRow = (row, policyData) => {
    const lobAdoptionList = policyData?.lobAdoptionDecisionList || [];

    switch (row.RuleHeader.RuleFlowGroup) {
      case PXDX: {
        return validatePXDX(lobAdoptionList, row);
      }
      case HISTORY_TRANSLATION: {
        return validatedHistoryTranslation(lobAdoptionList, row);
      }
      case DEPENDENCY: {
        return validatedDependency(lobAdoptionList, row);
      }
      case INCOMPATIBILITY: {
        return validatedIncompatibility(lobAdoptionList, row);
      }
      case DEMOGRAPHICS: {
        return validatedDemographics(lobAdoptionList, row);
      }
      default: {
        const newRow = structuredClone(row);
        delete newRow.errors;
        return newRow;
      }
    }
  };

  const updateRulesAfterEdit = (newRow, oldRow) => {
    // Run the specific validation for the row and get the result.
    // This adds or removes the `.errors` property on the row object.
    const validatedRow = runValidationForRow(newRow, policyData);

    const { index } = validatedRow;
    const editRule = workspaceController.getEditRule(index) || "";
    const flowGroup = validatedRow.RuleHeader?.RuleFlowGroup?.trim();
    const linkedHistoryKey = validatedRow.RuleHeader?.LinkedHistoryRuleKey;

    let newController = workspaceController.cloneController();

    //handle tracking activity changes to coverage criteria
    let newRowCC = newRow.RuleFacts.CoverageCriteriaList;
    let oldRowCC = oldRow.RuleFacts.CoverageCriteriaList;
    if (typeof newRowCC === "string") {
      newRowCC = newRowCC.trim();
    }
    newRowCC = sanitizeCC(newRowCC);
    const ccChanges = compareCCValues(oldRowCC, newRowCC);
    let activityUpdatesArray = activityUpdates || [];
    if (ccChanges.added.length > 0) {
      activityUpdatesArray = updateActivityTrackerArrayforClob(
        workspaceController?.workspace?.healthPlanKey,
        workspaceController?.workspace?.policyKey,
        "200",
        WORKFLOW_TRANSLATION,
        window.location.href,
        newRow.RuleHeader.RuleVersion,
        newRow.RuleHeader.ChangeControl,
        newRow.RuleHeader.PolicyBusinessRuleName,
        ccChanges.added,
        "add",
        "Coverage Criteria",
        activityUpdatesArray,
        newRow.RuleHeader.RuleFlowGroup
      );
    }
    if (ccChanges.removed.length > 0) {
      activityUpdatesArray = updateActivityTrackerArrayforClob(
        workspaceController?.workspace?.healthPlanKey,
        workspaceController?.workspace?.policyKey,
        "200",
        WORKFLOW_TRANSLATION,
        window.location.href,
        newRow.RuleHeader.RuleVersion,
        newRow.RuleHeader.ChangeControl,
        newRow.RuleHeader.PolicyBusinessRuleName,
        ccChanges.removed,
        "remove",
        "Coverage Criteria",
        activityUpdatesArray,
        newRow.RuleHeader.RuleFlowGroup
      );
    }

    //handle activity changes ot RuleEndDate
    const oldEndDate = oldRow.RuleFacts.RuleEndDate || "";
    const newEndDate = newRow.RuleFacts.RuleEndDate || "";

    if (oldEndDate !== newEndDate && newEndDate !== "") {
      activityUpdatesArray = updateActivityTrackerArrayforClob(
        workspaceController?.workspace?.healthPlanKey,
        workspaceController?.workspace?.policyKey,
        "200",
        WORKFLOW_TRANSLATION,
        window.location.href,
        newRow.RuleHeader.RuleVersion,
        newRow.RuleHeader.ChangeControl,
        newRow.RuleHeader.PolicyBusinessRuleName,
        newRow.RuleFacts.ProcedureCode,
        "end",
        "rule",
        activityUpdatesArray,
        newRow.RuleHeader.RuleFlowGroup
      );
    }

    const isNewOrVariance = [
      POLICY_STATUS_NEW,
      POLICY_STATUS_VARIANCE,
    ].includes(editRule);

    if (flowGroup === DUHS_RFG && linkedHistoryKey) {
      // Pass the fully validated row to the linked rules utility
      newController = updateLinkedRulesAfterEdit(
        validatedRow, // Use the row that now has the correct .errors object
        index,
        oldRow,
        isNewOrVariance,
        workspaceController
      );
    } else {
      // Fallback to original logic for non-linked rules
      if (isNewOrVariance) {
        // Set the controller with the row that includes the new validation status
        newController = newController.setRule(index, validatedRow);
      } else {
        const duplicateIndex = index + 1;
        newController = newController
          .setEditRule(
            index,
            editRule === POLICY_STATUS_VARIANCE
              ? POLICY_STATUS_VARIANCE
              : POLICY_STATUS_EDIT
          )
          .duplicateRule(index)
          // Use the validated row when setting the new duplicated rule
          .setRule(duplicateIndex, { ...validatedRow, index: duplicateIndex })
          .setEditRule(duplicateIndex, POLICY_STATUS_NEW_OR_VARIANCE)
          .incrementRuleVersion(duplicateIndex)
          .setRuleEffectiveDate(duplicateIndex, "");

        //Log activity when a rule is copied.
        activityUpdatesArray = updateActivityTrackerArrayforClob(
          workspaceController?.workspace?.healthPlanKey,
          workspaceController?.workspace?.policyKey,
          "200",
          WORKFLOW_TRANSLATION,
          window.location.href,
          workspaceController.getRuleVersion(duplicateIndex),
          newRow.RuleHeader.ChangeControl,
          newRow.RuleHeader.PolicyBusinessRuleName,
          newRow.RuleFacts.ProcedureCode,
          "copy",
          "rule",
          activityUpdatesArray,
          newRow.RuleHeader.RuleFlowGroup
        );
      }
    }

    setActivityUpdates(activityUpdatesArray);
    setWorkspaceController(newController);

    // Return the validated row so the DataGrid has the latest data
    return validatedRow;
  };

  const handleCancelAction = () => {
    setWorkspaceController(null);
    navigate(-1);
  };

  const handleDelete = (id) => {
    deletedRowIdRef.current = id;
    setEndDateOpen(true);
  };

  const saveEndDate = (endDate) => {
    deletedRowIdRef.current != null
      ? handleDeleteRow(endDate, deletedRowIdRef.current)
      : saveOnStartFromScratch(endDate);
  };

  const saveOnStartFromScratch = (endDate) => {
    let clonedWorkspaceController = workspaceController.cloneController();
    for (
      let i = workspaceController.clob.PolicyBusinessRulesList.length - 1;
      i >= 0;
      i--
    ) {
      if (workspaceController.getEditRule(i) === POLICY_STATUS_NEW) {
        clonedWorkspaceController.removeRule(i);
      } else {
        const formattedEndDate = endDate
          ? dayjs(endDate).format("D-MMM-YYYY")
          : "";
        clonedWorkspaceController
          .setEditRule(i, POLICY_STATUS_DELETED)
          .setRuleEndDate(i, formattedEndDate);
      }
    }
    setWorkspaceController(clonedWorkspaceController);
    setStartedFromScratch(true);
  };

  const handleDeleteRow = (endDate, id) => {
    const index = workspaceController.clob.PolicyBusinessRulesList.findIndex(
      (p) => p.index === id
    );
    if (workspaceController.getEditRule(index) === POLICY_STATUS_NEW) {
      setWorkspaceController(
        workspaceController.cloneController().removeRule(index)
      );
    } else {
      const formattedEndDate = endDate
        ? dayjs(endDate).format("D-MMM-YYYY")
        : "";
      setWorkspaceController(
        workspaceController
          .cloneController()
          .setEditRule(index, POLICY_STATUS_DELETED)
          .setRuleEndDate(index, formattedEndDate)
      );
    }
  };

  const handleRemoveInvalidRow = (index) => {
    const newController = workspaceController
      .cloneController()
      .removeRule(index);
    setWorkspaceController(newController);
  };

  const resizeEvent = useCallback(
    () => calculateGridHeight(topPanelRef, setGridHeight, 40),
    [topPanelRef, setGridHeight]
  );
  useGridResizeHook(resizeEvent);
  useEffect(() => {
    if (topPanelRef.current) {
      resizeEvent(); // Only fires once when .current is first set
    }
  }, [topPanelRef.current]);

  const handleAccordionToggle = () => {
    setTimeout(() => {
      resizeEvent();
    }, 400);
  };

  const openPlusTab = (event) => {
    setPlusTabAnchor(event.currentTarget);
  };

  const handleTabChange = (event, newValue) => {
    setLoading(true);
    setTimeout(() => {
      setTabValue(newValue);
    }, 100);
  };

  const [plusTabAnchor, setPlusTabAnchor] = useState("");

  const [loading, setLoading] = useState(true);

  const addTab = (tabKey) => {
    let tabIndex = tabOrder.get(tabKey);
    const dynamicTabsIndex = dynamicTabs.findIndex((t) => t.key === tabKey);
    setDynamicTabs(
      dynamicTabs.toSpliced(dynamicTabsIndex, 1, {
        key: tabKey,
        length: 0,
        display: true,
      })
    );
    setTabValue(tabIndex);
    closePlusTab();
  };

  const closePlusTab = () => {
    setPlusTabAnchor("");
  };

  const getKeyByValue = (map, searchValue) => {
    for (let [key, value] of map.entries()) {
      if (value === searchValue) return key;
    }
  };

  const handleValidate = (policyState, ctReviewData, submit) => {
    const activeRules = Object.values(policyRules)
      .flat()
      .filter((rule) => !rule.RuleFacts.RuleEndDate);

    const hasVarianceEditRule = Object.values(policyRules)
      .flat()
      .some((rule) => rule.RuleHeader.EditRule === "VARIANCE");

    // Check LOB variance decision
    const lobWithVariance = policyData.lobAdoptionDecisionList.find(
      (lob) =>
        lob.adoptionDecision === "ADOPT_WITH_VARIANCE" ||
        lob.adoptionDecision === "AWV"
    );
    if (lobWithVariance) {
      if (!activeRules || !hasVarianceEditRule) {
        return {
          valid: false,
          reason: "FAILED_VALIDATION",
          message: `${lobWithVariance.lobDesc || "A LOB"} requires at least one variance.`,
        };
      }
    }

    const ccMap = new Map(
      coverageCriteriaList.filter((cc) => !isNaN(cc)).map((cc) => [cc, false])
    );
    Object.values(policyRules)
      .flat()
      .filter(
        (rule) =>
          !rule.RuleFacts?.RuleEndDate &&
          rule.RuleFacts.CoverageCriteriaList &&
          typeof rule.RuleFacts.CoverageCriteriaList === "string"
      )
      .forEach((rule) => {
        const ccList = rule.RuleFacts.CoverageCriteriaList.split(",");
        ccList.forEach((cc) => {
          if (!isNaN(cc)) {
            if (!ccMap.get(cc)) {
              ccMap.set(cc, true);
            }
          }
        });
      });

    const absentCCList = [];
    ccMap.forEach((v, k) => {
      if (!v) absentCCList.push(k);
    });

    if (absentCCList.length > 0) {
      return {
        valid: false,
        reason: "FAILED_VALIDATION",
        message: `CC ${absentCCList.join(",")} does not have any active rules.`,
      };
    }
    if (
      submit &&
      policyState.policyWorkflowTaskCategory?.toLowerCase() === "translation" &&
      (policyState.policyWorkflowTaskStatus?.toLowerCase() === "peer review" ||
        policyState.policyWorkflowTaskStatus?.toLowerCase() === "pdc review")
    ) {
      if (!ctReviewData || !ctReviewData.reviewType || !ctReviewData.results) {
        return {
          valid: false,
          reason: "CT_REVIEW_REQUIRED",
          message: "C&T Review is required.",
        };
      }
    }
    return { valid: true }; // validation passed
  };

  const filterInvalidRules = (rows, validation) => {
    if (validation === FAILED_VALIDATION) {
      return rows.filter((row) => {
        return row.errors ? Object.keys(row.errors).length > 0 : false;
      });
    }
    return rows;
  };

  const updateTabLength = (tabKey, newLength) => (prevTabs) => {
    const tabIndex = prevTabs.findIndex((tab) => tab.key === tabKey);
    if (tabIndex !== -1) {
      const updatedTabs = [...prevTabs];
      updatedTabs[tabIndex] = {
        ...updatedTabs[tabIndex],
        length: newLength,
      };
      return updatedTabs;
    }
    return prevTabs;
  };
  const pxdxRows = useMemo(() => {
    if (!policyRules || !policyRules[PXDX]) return [];
    const filteredRows = filterInvalidRules(
      policyRules[PXDX].map((row) =>
        validatePXDX(policyData?.lobAdoptionDecisionList || [], row)
      ),
      selectedvalidation
    );
    setDynamicTabs(updateTabLength(PXDX, filteredRows.length));
    return filteredRows;
  }, [
    policyRules?.[PXDX],
    selectedvalidation,
    policyData?.lobAdoptionDecisionList,
  ]);

  const historyRows = useMemo(() => {
    if (!policyRules || !policyRules[HISTORY_TRANSLATION]) return [];
    const filteredRows = filterInvalidRules(
      policyRules[HISTORY_TRANSLATION].map((row) =>
        validatedHistoryTranslation(
          policyData?.lobAdoptionDecisionList || [],
          row
        )
      ),
      selectedvalidation
    );
    setDynamicTabs(updateTabLength(HISTORY_TRANSLATION, filteredRows.length));
    return filteredRows;
  }, [
    policyRules?.[HISTORY_TRANSLATION],
    policyData?.lobAdoptionDecisionList,
    selectedvalidation,
  ]);

  const dependencyRows = useMemo(() => {
    if (!policyRules || !policyRules[DEPENDENCY]) return [];
    const filteredRows = filterInvalidRules(
      policyRules[DEPENDENCY].map((row) =>
        validatedDependency(policyData?.lobAdoptionDecisionList || [], row)
      ),
      selectedvalidation
    );
    setDynamicTabs(updateTabLength(DEPENDENCY, filteredRows.length));
    return filteredRows;
  }, [
    policyRules?.[DEPENDENCY],
    policyData?.lobAdoptionDecisionList,
    selectedvalidation,
  ]);

  const inCompabilityRows = useMemo(() => {
    if (!policyRules || !policyRules[INCOMPATIBILITY]) return [];
    const filteredRows = filterInvalidRules(
      policyRules[INCOMPATIBILITY].map((row) =>
        validatedIncompatibility(policyData?.lobAdoptionDecisionList || [], row)
      ),
      selectedvalidation
    );
    setDynamicTabs(updateTabLength(INCOMPATIBILITY, filteredRows.length));
    return filteredRows;
  }, [
    policyRules?.[INCOMPATIBILITY],
    policyData?.lobAdoptionDecisionList,
    selectedvalidation,
  ]);

  const demographicRows = useMemo(() => {
    if (!policyRules || !policyRules[DEMOGRAPHICS]) return [];
    const filteredRows = filterInvalidRules(
      policyRules[DEMOGRAPHICS].map((row) =>
        validatedDemographics(policyData?.lobAdoptionDecisionList || [], row)
      ),
      selectedvalidation
    );
    setDynamicTabs(updateTabLength(DEMOGRAPHICS, filteredRows.length));
    return filteredRows;
  }, [
    policyRules?.[DEMOGRAPHICS],
    policyData?.lobAdoptionDecisionList,
    selectedvalidation,
  ]);

  const errorPresent = useMemo(() => {
    if (
      !policyRules ||
      !policyRules[PXDX] ||
      !policyRules[HISTORY_TRANSLATION]
    ) {
      return 0;
    }
    return (
      filterInvalidRules(
        policyRules[PXDX].map((row) =>
          validatePXDX(policyData?.lobAdoptionDecisionList || [], row)
        ),
        FAILED_VALIDATION
      ).length ||
      filterInvalidRules(
        policyRules[HISTORY_TRANSLATION].map((row) =>
          validatedHistoryTranslation(
            policyData?.lobAdoptionDecisionList || [],
            row
          )
        ),
        FAILED_VALIDATION
      ).length ||
      filterInvalidRules(
        policyRules[DEMOGRAPHICS].map((row) =>
          validatedDemographics(policyData?.lobAdoptionDecisionList || [], row)
        ),
        FAILED_VALIDATION
      ).length ||
      filterInvalidRules(
        policyRules[DEPENDENCY].map((row) =>
          validatedDependency(policyData?.lobAdoptionDecisionList || [], row)
        ),
        FAILED_VALIDATION
      ).length ||
      filterInvalidRules(
        policyRules[INCOMPATIBILITY].map((row) =>
          validatedIncompatibility(
            policyData?.lobAdoptionDecisionList || [],
            row
          )
        ),
        FAILED_VALIDATION
      ).length
    );
  }, [policyRules, policyData?.lobAdoptionDecisionList, filterInvalidRules]);

  const tabColor = (length) =>
    selectedvalidation === FAILED_VALIDATION && length > 0
      ? "red"
      : "primary.main";

  useEffect(() => {
    const fetchPolicyData = async () => {
      setLoading(true);
      try {
        const response = await api.policyData.getPolicyData(
          healthPlanKey,
          policyKey
        );
        if (response.status === 200 && response.data != null) {
          setPolicyData({
            policyTranslator: response.data.policyTranslator,
            translationReviewer: response.data.translationReviewer,
            translationStatus: response.data.translationStatus,
            reviewType: response.data.reviewType,
            overallAdoptionDecision: response.data.overallAdoptionDecision
              ? response.data.overallAdoptionDecision
              : "",
            lobAdoptionDecisionList: response.data.lobAdoptionDecisionList
              ? response.data.lobAdoptionDecisionList
              : [],
            policyChangeControlXrefDto:
              response.data.policyChangeControlXrefDto,
          });
          setPolicyHistoryData(response.data.policyHistoryList);
          setCTReviewData(response.data.policyCTReviewList);
        } else if (response.data == null) {
          throw new Error("API failed to return policy data.");
        }
      } catch (e) {
        dispatch(
          setDialog({ message: formErrorStrfromObj(e), title: "Error" })
        );
      } finally {
        setLoading(false);
      }
    };
    if (healthPlanKey && policyKey) fetchPolicyData();
  }, [healthPlanKey, policyKey]);

  useEffect(() => {
    let description = "";
    description =
      policyData.translationStatus.toLowerCase() === "peer review"
        ? "Translation"
        : policyData.translationStatus.toLowerCase() === "pdc review"
          ? "PDC Translation"
          : "Translation";

    // Find the first row where submittedDate is null or empty and reviewType matches description
    const matchingRow = ctReviewData?.find(
      (row) =>
        (!row.submittedDate || row.submittedDate === "") &&
        row.reviewType === description
    );

    setReviewData((prev) => ({
      ...prev,
      reviewType: description,
      ctReviewKey: matchingRow?.ctReviewKey,
      results: matchingRow?.results || "",
      comments: matchingRow?.comments || "",
    }));
  }, [ctReviewData]);
  const handleupdatePolicyData = (
    submit,
    policyHistoryData,
    ctReviewData,
    updateAssignessReviewerDto,
    policyChangeControlXrefDto
  ) => {
    const validation = handleValidate(policyState, ctReviewData, submit);
    let messages = [];

    // If validation fails, short-circuit (return immediately).
    if (submit && (!validation.valid || errorPresent)) {
      messages.push("Please fix the validation errors before submitting.");
      if (!validation.valid && validation.message) {
        messages.push(validation.message);
        dispatch(
          setDialog({
            title: "Validation Failure",
            message: messages.join("\n"),
          })
        );
        return;
      }

      if (errorPresent) {
        setselectedvalidation(FAILED_VALIDATION);
      }
      return;
    }

    updatePolicyData(
      dispatch,
      navigate,
      policyState,
      submit,
      workspaceController,
      policyHistoryData,
      ctReviewData,
      updateAssignessReviewerDto,
      policyChangeControlXrefDto,
      WORKFLOW_TRANSLATION,
      activityUpdates
    );
  };

  const handleSubmitForReview = React.useCallback(() => {
    setshouldSubmit(false);
    const updateAssignessReviewerDto =
      !policyData.policyTranslator && !policyData.translationReviewer
        ? null
        : {
            healthPlanKey: healthPlanKey,
            policyKey: policyKey,
            translation: policyData.policyTranslator,
            translationReviewer: policyData.translationReviewer,
            policyStage: "Translation",
          };
    handleupdatePolicyData(
      submitForReview,
      policyHistoryData.filter((history) => history.type === NEW_COMMENT),
      reviewData,
      updateAssignessReviewerDto,
      policyData.policyChangeControlXrefDto
    );
  }, [
    submitForReview,
    policyData.policyTranslator,
    policyData.translationReviewer,
    healthPlanKey,
    policyKey,
    handleupdatePolicyData,
    policyHistoryData,
    reviewData,
    policyData.policyChangeControlXrefDto,
  ]);
  const submitLock = useRef(false);
  useEffect(() => {
    if (shouldSubmit && filterResetted && !submitLock.current) {
      submitLock.current = true;
      handleSubmitForReview();
    } else if (!shouldSubmit) {
      submitLock.current = false;
    }
  }, [shouldSubmit, handleSubmitForReview, filterResetted]);
  if (!workspaceController || !workspaceController.workspace) {
    return <div>Loading...</div>;
  }

  return (
    <Box>
      <Box ref={topPanelRef} className="top-half">
        <TopPanelComponent
          resetFilter={resetFilter}
          policyStateData={policyState}
          deletedRowId={deletedRowIdRef}
          healthPlanDisplayName={policyState?.healthPlanDisplayName || ""}
          policyNumber={policyNumber}
          policyTitle={policyTitle}
          editable={editable}
          setEndDateOpen={setEndDateOpen}
          startedFromScratch={startedFromScratch}
          healthPlanKey={workspaceController?.workspace?.healthPlanKey || "-1"}
          policyKey={policyState?.policyKey}
          workspaceController={workspaceController}
          handleCancelAction={handleCancelAction}
          sx={{ mb: 4 }}
          policyVersion={location?.state?.policyVersion || ""}
          policyData={policyData}
          setPolicyData={setPolicyData}
          reviewData={reviewData}
          setReviewData={setReviewData}
          ctReviewData={ctReviewData}
        >
          <TranslationFilter
            ccList={coverageCriteriaList}
            lobList={lobList}
            selectedLob={selectedLob}
            setSelectedLob={setSelectedLob}
            setSelectedCC={setSelectedCC}
            policyRules={policyRules}
            selectedCC={selectedCC}
            selectedVariance={selectedVariance}
            setSelectedVariance={setSelectedVariance}
            selectedvalidation={selectedvalidation}
            setselectedvalidation={setselectedvalidation}
            selectedUseCase={selectedUseCase}
            setSelectedUseCase={setSelectedUseCase}
            onAccordionToggle={handleAccordionToggle}
            showVarianceFilter={true}
            showUseCaseFilter={false}
            policyData={policyData}
          />

          <Box
            sx={{
              display: "flex",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& button": {
                  textTransform: "none",
                },
              }}
            >
              {dynamicTabs.map(
                (tab) =>
                  tab.display && (
                    <Tab
                      sx={{
                        color: tabColor(tab.length),
                        [`&.Mui-selected`]: {
                          color: tabColor(tab.length),
                        },
                      }}
                      key={tab.key}
                      value={tabOrder.get(tab.key)}
                      label={`${tab.key} (${tab.length})`}
                    />
                  )
              )}
            </Tabs>
            {dynamicMenuItems.length !== 0 && (
              <IconButton onClick={openPlusTab}>
                <Add fontSize="small" />
              </IconButton>
            )}
            <Menu
              anchorEl={plusTabAnchor}
              onClose={closePlusTab}
              open={Boolean(plusTabAnchor)}
            >
              {dynamicMenuItems.map(({ key }) => {
                return (
                  <MenuItem key={key} onClick={() => addTab(key)}>
                    {key}
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </TopPanelComponent>
      </Box>
      <Box>
        {tabValue !== -1 && (
          <PolicyTabsComponent
            setLoading={setLoading}
            gridHeight={gridHeight}
            policyRules={policyRules}
            tabValue={tabValue}
            setTabValue={setTabValue}
            handleDelete={handleDelete}
            dynamicTabs={dynamicTabs}
            setDynamicTabs={setDynamicTabs}
            selectedCC={selectedCC}
            selectedVariance={selectedVariance}
            setSelectedVariance={setSelectedVariance}
            editable={editable}
            setSelectedCC={setSelectedCC}
            chips={coverageCriteriaList}
            availableProcedureCodes={availableProcedureCodes}
            newRowStructure={newRowStructure}
            addNewRows={addNewRows}
            duplicateRow={duplicateRow}
            updateRulesAfterEdit={updateRulesAfterEdit}
            policyData={policyData}
            dxLimitationValues={dxLimitationValues}
            pageComponent="PolicyTranslation"
            handleRemoveInvalidRow={handleRemoveInvalidRow}
            endExistingRule={endExistingRule}
            pxdxRows={pxdxRows}
            historyRows={historyRows}
            inCompabilityRows={inCompabilityRows}
            demographicRows={demographicRows}
            dependencyRows={dependencyRows}
          />
        )}
      </Box>
      <EndDateDialog
        deletedRowIdRef={deletedRowIdRef}
        endDateOpen={endDateOpen}
        setEndDateOpen={setEndDateOpen}
        saveEndDate={saveEndDate}
      />
    </Box>
  );
};
export default PolicyTranslation;
